import React, { useState, useEffect } from 'react';
import './Patterns.css';

const Patterns = () => {
  const [patterns, setPatterns] = useState([]);
  const [allImages, setAllImages] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedColorScheme, setSelectedColorScheme] = useState(null);
  const [showGallery, setShowGallery] = useState(false);
  
  const API_BASE = window.location.hostname === 'localhost' ? 
    'http://localhost:3000/uploads-api' : '/uploads-api';

  // Color schemes definitions
  const colorSchemes = {
    'GATSBY GOLD': { 
      gradient: 'linear-gradient(45deg, #FFB300, #FFD54F, #FF8F00)',
      colors: ['#FFB300', '#FFD54F', '#FF8F00']
    },
    'DECO TEAL': { 
      gradient: 'linear-gradient(45deg, #00897B, #4DB6AC, #00695C)',
      colors: ['#00897B', '#4DB6AC', '#00695C']
    },
    'ROYAL PURPLE': { 
      gradient: 'linear-gradient(45deg, #7B1FA2, #BA68C8, #4A148C)',
      colors: ['#7B1FA2', '#BA68C8', '#4A148C']
    },
    'SUNSET ORANGE': { 
      gradient: 'linear-gradient(45deg, #FF6F00, #FFB74D, #E65100)',
      colors: ['#FF6F00', '#FFB74D', '#E65100']
    },
    'EMERALD GREEN': { 
      gradient: 'linear-gradient(45deg, #2E7D32, #66BB6A, #1B5E20)',
      colors: ['#2E7D32', '#66BB6A', '#1B5E20']
    },
    'VINTAGE ROSE': { 
      gradient: 'linear-gradient(45deg, #C2185B, #F06292, #880E4F)',
      colors: ['#C2185B', '#F06292', '#880E4F']
    }
  };

  // Load all images and group by color scheme
  useEffect(() => {
    const loadPatterns = async () => {
      try {
        const response = await fetch(`${API_BASE}/images.php`);
        if (response.ok) {
          const data = await response.json();
          if (data.success) {
            setAllImages(data.images);
            
            // Group images by color scheme
            const colorSchemeGroups = {};
            
            // Initialize color schemes
            Object.keys(colorSchemes).forEach(scheme => {
              colorSchemeGroups[scheme] = [];
            });
            
            // Group uploaded images by their color scheme
            data.images.forEach(img => {
              if (img.section === 'patterns' && img.colorScheme) {
                const schemeName = img.colorScheme.name;
                if (colorSchemeGroups[schemeName]) {
                  colorSchemeGroups[schemeName].push(img);
                }
              }
            });
            
            // Create pattern cards
            const patternCards = Object.keys(colorSchemes).map(schemeName => ({
              id: schemeName.toLowerCase().replace(/\s+/g, '-'),
              name: schemeName,
              color: colorSchemes[schemeName].gradient,
              images: colorSchemeGroups[schemeName],
              count: colorSchemeGroups[schemeName].length,
              // Use first image as preview if available
              previewImage: colorSchemeGroups[schemeName][0]?.url || null
            }));
            
            setPatterns(patternCards);
          }
        }
      } catch (error) {
        console.error('Error loading patterns:', error);
        // Fallback to default patterns
        const fallbackPatterns = Object.keys(colorSchemes).map(schemeName => ({
          id: schemeName.toLowerCase().replace(/\s+/g, '-'),
          name: schemeName,
          color: colorSchemes[schemeName].gradient,
          images: [],
          count: 0,
          previewImage: null
        }));
        setPatterns(fallbackPatterns);
      } finally {
        setIsLoading(false);
      }
    };

    loadPatterns();
  }, []);

  return (
    <section className="patterns-section" id="patterns">
      <div className="zigzag"></div>
      
      <div className="section-header">
        <h2>PATTERN COLLECTION</h2>
        <div className="header-ornament">◆ ◆ ◆</div>
      </div>
      
      {showGallery && selectedColorScheme ? (
        <div className="pattern-gallery">
          <button className="back-button" onClick={() => setShowGallery(false)}>
            ← BACK TO COLLECTION
          </button>
          <h2>{selectedColorScheme.name} GALLERY</h2>
                 <div className="gallery-grid">
                   {selectedColorScheme.images.map((img) => (
                     <div key={img.id} className="gallery-item art-deco-border">
                       <img src={img.url} alt={img.title || img.name} className="gallery-image" />
                       <div className="gallery-info">
                         <p>{img.title || ''}</p>
                       </div>
                     </div>
                   ))}
                   {selectedColorScheme.images.length === 0 && (
                     <div className="empty-gallery">
                       <p>No images uploaded yet for this color scheme.</p>
                       <p>Upload images in the admin panel to see them here!</p>
                     </div>
                   )}
                 </div>
        </div>
      ) : isLoading ? (
        <div className="patterns-loading">
          <div className="loading-spinner">
            <div className="ornament">◆</div>
            <p>Loading patterns...</p>
          </div>
        </div>
      ) : (
        <div className="patterns-grid">
          {patterns.map((pattern) => (
            <div 
              key={pattern.id} 
              className="pattern-card art-deco-border clickable"
              onClick={() => setSelectedColorScheme(pattern)}
            >
              <div className="pattern-preview">
                {pattern.previewImage ? (
                  <>
                    <img src={pattern.previewImage} alt={pattern.name} className="pattern-image" />
                    <div className="pattern-overlay">
                      <div className="pattern-count">
                        {pattern.count} {pattern.count === 1 ? 'IMAGE' : 'IMAGES'}
                      </div>
                    </div>
                  </>
                ) : (
                  <div 
                    className="pattern-gradient"
                    style={{ background: pattern.color }}
                  >
                    <div className="pattern-overlay"></div>
                  </div>
                )}
              </div>
              <h3>{pattern.name}</h3>
              <div className="pattern-details">
                <span>◆</span>
                <span>●</span>
                <span>▲</span>
              </div>
              {pattern.count > 0 && (
                <div className="pattern-action">
                  <button 
                    className="view-gallery-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedColorScheme(pattern);
                      setShowGallery(true);
                    }}
                  >
                    VIEW GALLERY ({pattern.count})
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      
      <div className="zigzag"></div>
    </section>
  );
};

export default Patterns;